import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Shield,
  ShieldCheck,
  Lock,
  Search,
  Edit3,
  Trash2,
  X,
  Check,
  Key,
  UserX,
  Compass,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_META = {
  veneravel: { label: "Venerável Mestre", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", border: "border-[#D4AF37]/40" },
  secretaria: { label: "Secretaria", color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  tesoureiro: { label: "Tesoureiro", color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  irmao: { label: "Irmão", color: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  visitante: { label: "Visitante", color: "text-slate-300", bg: "bg-slate-700/40", border: "border-slate-600" },
};

/** Perfis multi-atribuíveis (checkboxes) — controlam módulos acessados dentro da Loja. */
const AVAILABLE_PROFILES = [
  { key: "Geral",             color: "bg-sky-500/15 text-sky-300 border-sky-500/40",           hint: "Vê o próprio cadastro e extrato" },
  { key: "Secretaria",        color: "bg-amber-500/15 text-amber-300 border-amber-500/40",     hint: "Gerencia Membros, Sessões e Balaústres" },
  { key: "Tesouraria",        color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", hint: "Gerencia mensalidades, receitas e despesas" },
  { key: "Chancelaria",       color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",         hint: "Livro de Presenças, Datas Comemorativas e Presença QR" },
  { key: "Venerável Mestre",  color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",   hint: "Presidência da Loja (cargo eleito por gestão)" },
  { key: "Master da Loja",    color: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",      hint: "Autonomia total (Diretoria + Transferência de posse)" },
];
const PROFILE_STYLE = Object.fromEntries(AVAILABLE_PROFILES.map((p) => [p.key, p.color]));

export const UsersView = ({ members = [] }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error("Erro ao carregar usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (u) => {
    if (u.type === "membro") {
      toast.error("Este usuário é um Irmão. Use o menu 'Membros da Loja' para removê-lo.");
      return;
    }
    if (!window.confirm(`Excluir o acesso de ${u.name}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success("Usuário excluído.");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir.");
    }
  };

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="users-view">
      {/* Info: Master da Loja vs Venerável Mestre */}
      <div className="p-3 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 text-[11px] text-slate-300 flex items-start gap-2" data-testid="master-vs-veneravel-hint">
        <Shield className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
        <div>
          <b className="text-[#D4AF37]">Master da Loja</b> é quem administra a conta na plataforma (permanente, ligado ao e-mail que fez o cadastro).
          O <b className="text-[#D4AF37]">Venerável Mestre</b> e demais cargos rituais são eleitos a cada gestão em
          <span className="text-white font-semibold"> Master da Loja → Diretoria da Gestão</span>. Perfis (Geral, Secretaria, etc.) controlam apenas as permissões dentro do sistema.
        </div>
      </div>

      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-emerald-400">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-emerald-400 font-bold mb-1">
              <Shield className="w-4 h-4" />
              <span>Secretaria • Controle de Acessos</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Usuários do Sistema</h2>
            <p className="text-xs text-slate-400 mt-1">
              Gerencie logins, permissões e status de acesso dos Irmãos e administradores.
            </p>
          </div>
          <button
            data-testid="users-btn-new"
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(16,185,129,0.25)]"
          >
            <UserPlus className="w-4 h-4" />
            <span>Novo Usuário</span>
          </button>
        </div>

        <div className="mt-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="users-input-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou email..."
            className="w-full bg-[#0B111E] border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
          />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {Object.entries(ROLE_META).map(([role, meta]) => {
          const count = users.filter((u) => u.role === role).length;
          return (
            <div key={role} className={`luxury-card p-4 border ${meta.border}`}>
              <div className="text-[10px] uppercase text-slate-400 font-bold">{meta.label}</div>
              <div className={`text-2xl font-serif font-bold mt-1 ${meta.color}`}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="luxury-card p-0 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-[#080D18] text-left text-[11px] uppercase tracking-wider text-slate-400">
              <th className="py-3 px-4">Usuário</th>
              <th className="py-3 px-4">E-mail</th>
              <th className="py-3 px-4">Cargo</th>
              <th className="py-3 px-4">Perfis</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500">
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-slate-500 text-sm">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
            {filtered.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.irmao;
              const profiles = u.roles && u.roles.length > 0
                ? u.roles
                : [meta.label];
              return (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-b border-slate-800/60 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300 font-bold text-xs">
                        {u.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <div className="font-semibold text-white text-sm">{u.name}</div>
                        {u.office && <div className="text-[10px] text-slate-400">{u.office}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-300 text-xs font-mono">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full border ${meta.color} ${meta.bg} ${meta.border}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1" data-testid={`user-profiles-${u.id}`}>
                      {profiles.map((p) => (
                        <span
                          key={p}
                          className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${PROFILE_STYLE[p] || "bg-slate-700/40 text-slate-300 border-slate-600"}`}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {u.is_active ? (
                      <span className="text-[10px] uppercase font-bold text-emerald-300 flex items-center space-x-1">
                        <Check className="w-3 h-3" />
                        <span>Ativo</span>
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold text-rose-300 flex items-center space-x-1">
                        <UserX className="w-3 h-3" />
                        <span>Inativo</span>
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right space-x-1">
                    <button
                      data-testid={`user-btn-edit-${u.id}`}
                      onClick={() => {
                        setEditing(u);
                        setShowModal(true);
                      }}
                      title="Editar / trocar senha"
                      className="inline-flex p-1.5 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"
                    >
                      <Key className="w-3.5 h-3.5" />
                    </button>
                    {u.type !== "membro" && (
                      <button
                        data-testid={`user-btn-delete-${u.id}`}
                        onClick={() => handleDelete(u)}
                        className="inline-flex p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSaved={() => {
            setShowModal(false);
            setEditing(null);
            fetchUsers();
          }}
          initialData={editing}
          members={members}
        />
      )}
    </div>
  );
};

const UserModal = ({ isOpen, onClose, onSaved, initialData, members }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "irmao",
    roles: ["Geral"],
    member_id: "",
    is_active: true,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        email: initialData.email || "",
        password: "",
        role: initialData.role || "irmao",
        roles: Array.isArray(initialData.roles) && initialData.roles.length > 0 ? initialData.roles : ["Geral"],
        member_id: initialData.member_id || "",
        is_active: initialData.is_active !== false,
      });
    } else {
      setForm({ name: "", email: "", password: "", role: "irmao", roles: ["Geral"], member_id: "", is_active: true });
    }
  }, [initialData]);

  const toggleProfile = (profile) => {
    setForm((f) => {
      const has = f.roles.includes(profile);
      return { ...f, roles: has ? f.roles.filter((r) => r !== profile) : [...f.roles, profile] };
    });
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.roles || form.roles.length === 0) {
      toast.error("Selecione pelo menos 1 perfil.");
      return;
    }
    try {
      if (initialData?.id) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/users/${initialData.id}`, payload);
        toast.success("Usuário atualizado.");
      } else {
        if (!form.password) {
          toast.error("Defina uma senha inicial para o novo usuário.");
          return;
        }
        await axios.post(`${API}/users`, form);
        toast.success("Usuário criado com sucesso.");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar usuário.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="user-modal"
    >
      <div
        className="w-full max-w-lg bg-[#0B111E] border border-emerald-500/40 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-800 bg-[#080D18] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold flex items-center space-x-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Secretaria • {initialData ? "Editar" : "Novo"} Usuário</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              {initialData?.name || "Novo acesso ao sistema"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Nome *</label>
            <input
              data-testid="user-input-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">E-mail *</label>
            <input
              data-testid="user-input-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
              <Lock className="w-3 h-3" />
              <span>Senha {initialData ? "(deixe em branco para manter)" : "*"}</span>
            </label>
            <input
              data-testid="user-input-password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Cargo principal *</label>
              <select
                data-testid="user-input-role"
                required
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
              >
                {Object.entries(ROLE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Status</label>
              <select
                data-testid="user-input-active"
                value={form.is_active ? "true" : "false"}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === "true" })}
                className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
              >
                <option value="true">Ativo</option>
                <option value="false">Inativo (bloqueado)</option>
              </select>
            </div>
          </div>

          {/* Perfis multi (checkboxes) */}
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1">
              <ShieldCheck className="w-3 h-3" />
              <span>Perfis de acesso *</span>
              <span className="text-slate-600 normal-case tracking-normal">— marque quais módulos ele acessa</span>
            </label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="user-profiles-checkboxes">
              {AVAILABLE_PROFILES.map((p) => {
                const checked = form.roles.includes(p.key);
                return (
                  <label
                    key={p.key}
                    data-testid={`user-profile-checkbox-${p.key}`}
                    className={`flex flex-col gap-0.5 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                      checked
                        ? `${p.color} ring-1 ring-current`
                        : "bg-[#0f1727] border-slate-700 text-slate-500 hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleProfile(p.key)}
                        className="w-3.5 h-3.5 rounded accent-emerald-500 cursor-pointer"
                      />
                      <span className="text-[11px] uppercase tracking-widest font-bold">{p.key}</span>
                    </div>
                    <span className="text-[9px] normal-case tracking-normal text-slate-400 leading-tight pl-6">
                      {p.hint}
                    </span>
                  </label>
                );
              })}
            </div>
            {form.roles.length === 0 && (
              <p className="mt-1.5 text-[10px] text-rose-400">Selecione pelo menos 1 perfil.</p>
            )}
            {form.roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Selecionados:</span>
                {form.roles.map((r) => (
                  <span
                    key={r}
                    className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${PROFILE_STYLE[r] || "bg-slate-700/40 text-slate-300 border-slate-600"}`}
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">Irmão vinculado (opcional)</label>
            <select
              data-testid="user-input-member"
              value={form.member_id || ""}
              onChange={(e) => setForm({ ...form, member_id: e.target.value })}
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-400 outline-none"
            >
              <option value="">— Nenhum (administrador externo) —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name} — CIM {m.cim}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold px-4"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="user-btn-submit"
              className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
            >
              <Check className="w-3.5 h-3.5" />
              <span>{initialData ? "Atualizar" : "Criar Usuário"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
